import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { NoveltyCheckReportContext } from '../novelty-check-report-context';
import type { NoveltyCheckReportState } from '../novelty-check-report-state';
import { InitialState } from './initial-state';
import { SearchFinishedState } from './search-finished-state';
import { ResultState } from './result-state';
import { NoveltyCheckTaskStep } from '@/domains/novelty-check-report/enums';
import { NoveltyCheckTaskStatusService } from '../../task-status-service';
import { SearchInputState } from './search-input-state';
import { SummaryState } from './summary-state';
import { RetrievalElementIdentifyStartState } from './retrieval-element-identify-start-state';
import { PatentConfirmState } from './patent-confirm-state';
import { GenerateFeatureCompareTableState } from './generate-feature-compare-table-state';
import { CommandService, Logger } from '@patsnap/tahiti-core';
import { STEPS_ORDER } from '../../../constants';
import { NoveltyCheckStepInfoStatus, type NoveltyCheckStepInfo } from '@/domains/novelty-check-report/types';
import { NoveltyCheckStepInfoModel } from '@/domains/novelty-check-report/models/novelty-check-step-info-model';
import { NoveltySearchTechFeatureState } from './novelty-search-tech-feature-state';
import { RetrievalResultReportModel } from '@/domains/novelty-check-report/models/retrieval-result-report-model';
import { NoveltySearchStorageService } from '../../novelty-search-storage-service';

@injectable()
export class NoveltyCheckReportContextImpl implements NoveltyCheckReportContext {
    private _currentState!: NoveltyCheckReportState;
    private _states: Map<NoveltyCheckTaskStep, NoveltyCheckReportState> = new Map();

    // eslint-disable-next-line max-params
    constructor(
        @inject(Logger)
        private readonly _logger: Logger,
        @inject(CommandService)
        private readonly _commandService: CommandService,
        @inject(NoveltyCheckTaskStatusService)
        private readonly _noveltyCheckTaskStatusService: NoveltyCheckTaskStatusService,
        @inject(RetrievalResultReportModel)
        private readonly _retrievalResultReportModel: RetrievalResultReportModel,
        @inject(NoveltySearchStorageService)
        private readonly _noveltySearchStorageService: NoveltySearchStorageService,
        @inject(InitialState) initialState: InitialState,
        @inject(SummaryState) summaryState: SummaryState,
        @inject(SearchInputState) searchInputState: SearchInputState,
        @inject(NoveltySearchTechFeatureState) noveltySearchTechFeatureState: NoveltySearchTechFeatureState,
        @inject(RetrievalElementIdentifyStartState) retrievalElementIdentifyStartState: RetrievalElementIdentifyStartState,
        @inject(SearchFinishedState) confirmState: SearchFinishedState,
        @inject(PatentConfirmState) patentConfirmState: PatentConfirmState,
        @inject(GenerateFeatureCompareTableState) generateFeatureCompareTableState: GenerateFeatureCompareTableState,
        @inject(ResultState) resultState: ResultState,
        @inject(NoveltyCheckStepInfoModel)
        private readonly _noveltyCheckStepInfoModel: NoveltyCheckStepInfoModel,
    ) {
        // 初始化所有状态
        this._states.set(NoveltyCheckTaskStep.INITIAL, initialState);
        this._states.set(NoveltyCheckTaskStep.SUMMARY, summaryState);
        this._states.set(NoveltyCheckTaskStep.NOVELTY_SEARCH_INPUT, searchInputState);
        this._states.set(NoveltyCheckTaskStep.NOVELTY_SEARCH_TECH_FEATURE, noveltySearchTechFeatureState);
        this._states.set(NoveltyCheckTaskStep.NOVELTY_SEARCH_RETRIEVAL_ELEMENTS, retrievalElementIdentifyStartState);
        this._states.set(NoveltyCheckTaskStep.SEARCH_FINISHED, confirmState);
        this._states.set(NoveltyCheckTaskStep.PATENT_CONFIRM, patentConfirmState);
        this._states.set(NoveltyCheckTaskStep.GENERATE_FEATURE_COMPARE_TABLE, generateFeatureCompareTableState);
        this._states.set(NoveltyCheckTaskStep.REPORT_CREATED, resultState);
    }



    getState(step: NoveltyCheckTaskStep): NoveltyCheckReportState {
        const state = this._states.get(step);
        if (!state) {
            throw new Error(`未找到状态: ${step}`);
        }
        return state;
    }

    getStepInfo(): NoveltyCheckStepInfo {
        return this._noveltyCheckStepInfoModel.getStepInfo(this._getStep(this._currentState));
    }

    private _getStep(state: NoveltyCheckReportState): NoveltyCheckTaskStep {
        for (const [step, s] of this._states.entries()) {
            if (s === state) {
                return step;
            }
        }
        throw new Error('未找到步骤');
    }

    setStepStatus(state: NoveltyCheckReportState, status: NoveltyCheckStepInfoStatus) {
        this._noveltyCheckStepInfoModel.setStepStatus(this._getStep(state), status);
    }

    async transitionTo(state: NoveltyCheckReportState): Promise<void> {
        if (this._currentState === state) {
            return;
        }

        // 切换状态
        this._currentState = state;

        this.setStepStatus(state, NoveltyCheckStepInfoStatus.ENTERING);
        try {
            // 执行新状态的进入逻辑
            await this._currentState.enter(this);
            this.setStepStatus(state, NoveltyCheckStepInfoStatus.IDLE);
        } catch (error) {
            this.setStepStatus(state, NoveltyCheckStepInfoStatus.ERROR);
            throw error;
        }
    }

    async next(): Promise<void> {
        const state = this._currentState;
        console.log('next state', state);
        this.setStepStatus(state, NoveltyCheckStepInfoStatus.LEAVING);
        try {
            await this._currentState.handleNext(this);
            this.setStepStatus(state, NoveltyCheckStepInfoStatus.LEFT);
        } catch (error) {
            this.setStepStatus(state, NoveltyCheckStepInfoStatus.ERROR);
            throw error;
        }
    }

    async previous(): Promise<void> {
        // 获取当前步骤
        const currentStep = this._getStep(this._currentState);

        // 清除当前步骤的所有状态
        this._noveltyCheckStepInfoModel.resetStep(currentStep);

        // 处理上一步逻辑，让状态对象处理返回
        await this._currentState.handlePrevious(this);
    }

    async restoreBack(state: NoveltyCheckReportState): Promise<void> {
        // 获取当前步骤和目标步骤
        const currentStep = this._getStep(this._currentState);
        const targetStep = this._getStep(state);

        // 检查目标状态是否在当前状态之前
        const currentIndex = STEPS_ORDER.indexOf(currentStep);
        const targetIndex = STEPS_ORDER.indexOf(targetStep);

        if (targetIndex > currentIndex) {
            throw new Error(`无法恢复到后续状态: ${targetStep}`);
        }

        if (targetIndex === currentIndex) {
            return; // 已经是目标状态，无需操作
        }

        // 从当前状态开始，逐步返回到目标状态（不包括目标状态本身）
        for (let i = currentIndex; i > targetIndex; i--) {
            const stepToReset = STEPS_ORDER[i];

            // 清除当前步骤的所有状态
            this._noveltyCheckStepInfoModel.resetStep(stepToReset);

            // 获取当前步骤对应的状态对象
            const currentStateToReset = this.getState(stepToReset);

            // 处理返回逻辑，让状态对象处理返回
            await currentStateToReset.handlePrevious(this);
        }

        // 直接设置当前状态为目标状态，不调用 enter 函数
        this._currentState = state;
    }

    async initialize(): Promise<void> {
        // 检查是否有从首页传递过来的搜索数据
        // if (this._noveltySearchStorageService.hasStorageData()) {
        //     const storageData = this._noveltySearchStorageService.getStorageData();
        //     if (storageData) {
        //         this._logger.info('检测到首页传递的搜索数据，自动填充并开始搜索');
        //         const currentState = this.getState(NoveltyCheckTaskStep.INITIAL);
        //         this.transitionTo(currentState);
        //         // 更新 RetrievalResultReportModel
        //         this._retrievalResultReportModel.update((model) => {
        //             model.techEssential = storageData.techEssential;
        //             if (storageData.pbdStart) {
        //                 model.pbdStart = storageData.pbdStart;
        //             }
        //             if (storageData.pbdEnd) {
        //                 model.pbdEnd = storageData.pbdEnd;
        //             }
        //             return model;
        //         });

        //         // 清空存储，避免重复执行
        //         this._noveltySearchStorageService.clearStorageData();
        //         this.next();
        //         return;
        //     }
        // }

        // 获取任务状态
        const taskStatus = await this._noveltyCheckTaskStatusService.pullTaskStatus();
        // 如果是初始化的任务状态,说明此任务不存在,直接跳转到
        if (taskStatus.taskStep === NoveltyCheckTaskStep.INITIAL) {
            this._commandService.executeCommand('command:route:home', {
                tab: 'ai_novelty_search',
            });
            return;
        }
        for (const step of STEPS_ORDER) {
            const currentState = this.getState(step);
            if (taskStatus.taskStep === step) {
                this.transitionTo(currentState);
                return;
            }
            await this.transitionTo(currentState);
            this.setStepStatus(currentState, NoveltyCheckStepInfoStatus.LEFT);
        }
    }
}
